import { ClipboardList, Heart, Menu, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';

import { NAV_LINKS } from '@/data/navigation';
import { useCartActions, useCartSummary } from '@/features/cart/useCart';
import { useFavorites } from '@/features/favorites/favorites.context';
import { cn } from '@/lib/cn';
import { WA_LINKS } from '@/lib/whatsapp';

import { Container } from '../common/Container';
import { WhatsAppIcon } from '../common/icons';
import { Logo } from '../common/Logo';
import { Button } from '../ui/button';
import { MobileMenu } from './MobileMenu';

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openCart } = useCartActions();
  const { itemCount } = useCartSummary();
  const { count: favoriteCount } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();

  // Tighten the header once the page scrolls, so the hero gets the full stage.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Any navigation closes the mobile menu.
  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 bg-cream-100/95 backdrop-blur-md',
        'transition-shadow duration-300',
        scrolled ? 'border-b border-sand-300 shadow-card' : 'border-b border-transparent',
      )}
    >
      <Container className="flex h-header items-center gap-3 lg:h-header-lg">
        <Logo />

        <nav aria-label="Navegação principal" className="hidden flex-1 justify-center lg:flex">
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={!link.matchPrefix && link.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'rounded-lg px-3 py-2 font-display text-sm font-bold whitespace-nowrap transition-colors',
                      isActive && link.to !== '/'
                        ? 'text-orange-600'
                        : 'text-brown-700 hover:bg-cream-300 hover:text-brown-900',
                    )
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1.5 lg:gap-2">
          <IconAction
            label="Buscar produtos"
            icon={<Search />}
            onClick={() => navigate('/catalogo')}
            className="hidden sm:grid"
          />

          <IconAction
            label={`Favoritos${favoriteCount ? ` (${favoriteCount})` : ''}`}
            icon={<Heart />}
            count={favoriteCount}
            onClick={() => navigate('/favoritos')}
            className="hidden sm:grid"
          />

          <IconAction
            label={`Abrir minha lista${itemCount ? ` com ${itemCount} itens` : ''}`}
            icon={<ClipboardList />}
            count={itemCount}
            onClick={openCart}
            highlight
          />

          {/*
            Client feedback on the first draft: the WhatsApp button dominated
            the header. It stays present and green, but sized as a peer of the
            cart rather than a banner across the top.
          */}
          <Button asChild variant="whatsapp" size="sm" className="hidden lg:inline-flex">
            <a href={WA_LINKS.general} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon aria-hidden="true" />
              WhatsApp
            </a>
          </Button>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            className="grid size-11 place-items-center rounded-xl text-brown-800 transition-colors hover:bg-cream-300 lg:hidden"
          >
            <Menu className="size-6" aria-hidden="true" />
          </button>
        </div>
      </Container>

      <MobileMenu open={menuOpen} onOpenChange={setMenuOpen} />
    </header>
  );
}

interface IconActionProps {
  label: string;
  icon: React.ReactNode;
  count?: number;
  highlight?: boolean;
  onClick: () => void;
  className?: string;
}

function IconAction({ label, icon, count = 0, highlight, onClick, className }: IconActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'relative grid size-11 place-items-center rounded-xl transition-all',
        highlight
          ? 'bg-brown-900 text-cream-100 hover:-translate-y-0.5 hover:bg-brown-800'
          : 'text-brown-800 hover:bg-cream-300',
        '[&_svg]:size-5',
        className,
      )}
    >
      {icon}
      {count > 0 && (
        <span
          className={cn(
            'absolute -top-1 -right-1 grid min-w-5 place-items-center rounded-full px-1 font-display',
            'bg-orange-500 text-[11px] font-bold text-white',
            'animate-pop-in ring-2 ring-cream-100',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}
